import useApi from './useApi'
import { buildB2bDetailsByKeywordUrl } from 'dataSource/ProviderSearch/routes'
import { useDispatch } from 'react-redux'
import { setB2BDetails } from 'state/booking/actions'
import _omit from 'lodash/omit'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import useThriveAuth from './useThriveAuth'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import useAppSelector from './useAppSelector'
import useGetTreatment from './treatments/useGetTreatment'
import {
  addKeywordToStoredSearchQuery,
  extractKeywordFromQuery,
  formatAndValidateB2bDetails,
} from 'utils/b2bDetails'
import { hasError } from 'utils/common'
import RudderStackContext from 'context/common/RudderStackContext'
import { isSplitTimedOut } from 'utils/treatment'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'

type UseB2bDetailsReturnType = {
  isB2bDetailsLoading: boolean
  b2bDetailsError: AlertMessage | null
}

const useB2bDetailsByKeyword = (): UseB2bDetailsReturnType => {
  const [isB2bDetailsLoading, setIsB2bDetailsLoading] = useState<boolean>(true)
  const [b2bDetailsError, setB2bDetailsError] = useState<AlertMessage | null>(
    null
  )
  const { b2bUserDetails } =
    useAppSelector((state) => state.bookingHelper) ?? {}
  const { keyword } = b2bUserDetails ?? {}
  const dispatch = useDispatch()
  const { request: b2bPartnerRequest } = useApi()
  const { isPatient } = useThriveAuth()
  const router = useRouter()
  const { asPath, query, isReady } = router
  const { analyticsConsentGranted } = useContext(RudderStackContext)
  const splitTimedOut = isSplitTimedOut()
  const rolloutPlanSettled = useIsRolloutPlanSettled()
  const b2bPartnerSelfBookingTreatment = useGetTreatment(
    TreatmentFlags.B2B_PARTNERSHIP_SELF_BOOKING
  )

  const setUrlReferralPartnership = async (partnershipCode: string) => {
    try {
      if (!isB2bDetailsLoading) {
        setIsB2bDetailsLoading(true)
      }

      const b2bPartnerDetails = await b2bPartnerRequest.get(
        buildB2bDetailsByKeywordUrl(partnershipCode)
      )

      hasError(b2bPartnerRequest, b2bPartnerDetails)

      if (!!b2bPartnerDetails?.id) {
        addKeywordToStoredSearchQuery(partnershipCode)
      }

      const formattedB2bDetails = formatAndValidateB2bDetails(
        b2bPartnerDetails,
        partnershipCode,
        isPatient
      )
      dispatch(setB2BDetails(formattedB2bDetails))
    } catch (error) {
      setB2bDetailsError({
        timestamp: new Date().getTime(),
        message: error?.message,
      })
    } finally {
      setIsB2bDetailsLoading(false)
    }
  }

  /*
    This function extracts b2b partner keyword from query params and returns it.
    The keyword can be present directly in the query params in case user is already
    logged in. Otherwise, in case of non logged in state, the keyword will be
    available in redirectTo query param.
  */

  //For referrals, pcp type keywords set B2b details based on the keyword in url
  useEffect(() => {
    // Consent denied: wait for the rollout plan so a 100% released ON can still
    // apply the referral code. CONTROL after settle means the flag is not
    // released — clear loading instead of waiting forever.
    if (!analyticsConsentGranted) {
      if (!rolloutPlanSettled) {
        return
      }

      if (b2bPartnerSelfBookingTreatment === treatmentFlagValues.CONTROL) {
        setIsB2bDetailsLoading(false)

        return
      }
    }

    // Consent granted but the anonymous SDK timed out: CONTROL is terminal —
    // clear loading instead of waiting forever for a non-control treatment.
    if (
      analyticsConsentGranted &&
      splitTimedOut &&
      b2bPartnerSelfBookingTreatment === treatmentFlagValues.CONTROL
    ) {
      setIsB2bDetailsLoading(false)

      return
    }

    if (
      b2bPartnerSelfBookingTreatment !== treatmentFlagValues.CONTROL &&
      isReady
    ) {
      const isB2bPartnerSelfBookingOn =
        b2bPartnerSelfBookingTreatment === treatmentFlagValues.ON

      if (isB2bPartnerSelfBookingOn) {
        const b2bPartnershipCode = extractKeywordFromQuery(query)

        if (!!b2bPartnershipCode && b2bPartnershipCode !== keyword) {
          setUrlReferralPartnership(b2bPartnershipCode as string)
        } else {
          if (!b2bPartnershipCode) {
            dispatch(setB2BDetails())
          }

          setIsB2bDetailsLoading(false)
        }
      } else {
        setIsB2bDetailsLoading(false)
      }
    }
  }, [
    asPath,
    b2bPartnerSelfBookingTreatment,
    isReady,
    analyticsConsentGranted,
    splitTimedOut,
    rolloutPlanSettled,
  ])

  return {
    isB2bDetailsLoading,
    b2bDetailsError,
  }
}

export default useB2bDetailsByKeyword
